import React, { useEffect, useRef, useState } from 'react';
import VideoChatBubble from './VideoChatBubble';
import ScrollDownButton from '../Button/ScrollDownButton';
import { useTranslation } from 'react-i18next';
import useApi from '../../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { otpModalState } from '../../recoil/otp/otpModalAtom';
import { useInView } from 'react-intersection-observer';
import useDialog from '../../hooks/useDialog';
import { likeCountState, liveStatusState, liveUserCountState } from '../../recoil/chatCount/chatCountAtom';
import ERROR_CODES from '../../constants/errorCodes';
import * as Sentry from '@sentry/react';

const VideoChatWrapper = ({ id }) => {
  const contentRef = useRef(null);
  const [isButtonShow, setIsButtonShow] = useState(false);
  const [chatText, setChatText] = useState('');
  const [chatList, setChatList] = useState([]);
  const [prevKey, setPrevKey] = useState(null);
  const [nextKey, setNextKey] = useState(null);
  const setLikeCount = useSetRecoilState(likeCountState);
  const setLiveUserCount = useSetRecoilState(liveUserCountState);
  const setLiveStatus = useSetRecoilState(liveStatusState);
  const textareaRef = useRef(null);
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const setOtpLogin = useSetRecoilState(otpModalState);
  const type_id = id;
  const accessToken = localStorage.getItem('access_token');
  const type = 'live_clip';
  const [ref, inView] = useInView();
  const { showDialog } = useDialog();
  const isMountedRef = useRef(true);

  const clearSession = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('exp');
    Sentry.setUser(null);
  };

  // 신고 완료 dialog
  const alertReport = () => {
    showDialog({
      dialogType: 'AlertDialog',
      dialogProps: {
        title: `${t('complete_report_msg')}.`,
        confirmText: t('confirm'),
      },
    });
  };

  // 이미 신고된 메세지에 대한 dialog
  const alertAlreadyReport = () => {
    showDialog({
      dialogType: 'AlertDialog',
      dialogProps: {
        title: `${t('already_report_msg')}.`,
        confirmText: t('confirm'),
      },
    });
  };

  const fetchNewLiveChat = async (key_type = 'next') => {
    let key_param = '';
    if (key_type === 'next' && nextKey) {
      key_param = `&key=${nextKey}`;
    } else if (key_type === 'prev' && prevKey) {
      key_param = `&key=${prevKey}`;
    }

    try {
      const response = await api.get(`live_chat/${type}/${type_id}?key_type=${key_type}${key_param}`, {
        headers: {
          Authorization: `${accessToken}`,
        },
      });

      // VideoInfoWrapper에서 사용하는 전역 데이터
      setLikeCount(response?.data?.likeCount);
      setLiveUserCount(response?.data?.live_user_count);
      setLiveStatus(response?.data?.live_status);

      // prevKey 갱신
      const newPrevKey = response?.data?.prevKey;
      if (newPrevKey) setPrevKey(newPrevKey);

      // nextKey 갱신
      const newNextKey = response?.data?.nextKey;
      if (newNextKey) setNextKey(newNextKey);

      let newChatList = response?.data?.chatList;
      const deleteList = response?.data?.deleteList;

      // deleteList에 있는 데이터 삭제
      if (deleteList && deleteList.length > 0) {
        let updatedChatList = chatList.filter((chat) => !deleteList.includes(chat.chatId));
        setChatList(updatedChatList);
      }

      // newChatList에 있는 데이터 삽입
      if (newChatList && newChatList.length > 0) {
        let combinedChatList;

        if (key_type === 'next') {
          combinedChatList = [...chatList, ...newChatList];
        } else {
          // key_type === 'prev'
          combinedChatList = [...newChatList, ...chatList];
        }

        combinedChatList.sort((a, b) => a.timestamp - b.timestamp);
        setChatList(combinedChatList);
      }

      if (contentRef.current) {
        let bottomGap = 115; // 스크롤 바닥에서부터 스크롤 최하단 이동 버튼이 노출될 위치 조절
        const isAtBottom =
          contentRef.current.scrollHeight - bottomGap < contentRef.current.scrollTop + contentRef.current.clientHeight;

        // 스크롤이 바닥에 거의 닿았을 때만
        if (isAtBottom) {
          setTimeout(() => {
            if (isMountedRef.current) {
              handleScrollDownButtonClick();
            }
          }, 200);
        }
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_BAD_RESPONSE') {
        navigate('/error');
      }

      const errorCode = error.response && error.response.data && error.response.data.code;
      if (
        errorCode === ERROR_CODES.invalidToken ||
        errorCode === ERROR_CODES.tokenExpired ||
        errorCode === ERROR_CODES.unauthorized
      ) {
        clearSession();
        navigate('/');
        setOtpLogin(true); // 로그인 모달
      } else {
        console.log(errorCode);
      }
    }
  };

  const sendLiveChat = async () => {
    const contentInfo = {
      content: chatText,
    };

    try {
      await api.post(`live_chat/${type}/${type_id}`, contentInfo, {
        headers: {
          Authorization: `${accessToken}`,
        },
      });
      fetchNewLiveChat();
      setTimeout(() => {
        if (isMountedRef.current) {
          handleScrollDownButtonClick();
        }
      }, 200);
    } catch (error) {
      const errorCode = error.response && error.response.data && error.response.data.code;
      if (
        errorCode === ERROR_CODES.invalidToken ||
        errorCode === ERROR_CODES.tokenExpired ||
        errorCode === ERROR_CODES.unauthorized
      ) {
        navigate('/');
        clearSession();
        setOtpLogin(true); // 로그인 모달
      } else {
        console.log(errorCode);
      }
    } finally {
      setChatText('');
      autoResizeTextarea();
    }
  };

  const handleScrollDownButtonClick = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const autoResizeTextarea = () => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      const height = textarea.scrollHeight;
      textarea.style.height = `${height}px`;
    }
  };

  // 스크롤 올렸을 때 최상단에 닿으면 이전 채팅 호출
  const fetchPrevLiveChat = () => {
    if (prevKey) {
      fetchNewLiveChat('prev');
    }
  };

  const handleInput = (e) => {
    if (e.key === 'Enter') {
      if (e.nativeEvent.isComposing) return;
      e.preventDefault();
      sendLiveChat();
    } else {
      setChatText(e.target.value);
      autoResizeTextarea();
    }
  };

  const deleteLiveChat = async (timestamp) => {
    const type = 'live_clip';

    try {
      await api.delete(`live_chat/${type}/${type_id}/${timestamp}`, {
        headers: {
          Authorization: `${accessToken}`,
        },
      });
      fetchNewLiveChat();
    } catch (error) {
      console.log(error);
    }
  };

  const reportLiveChat = async (timestamp, userNo) => {
    const type = 'live_clip';

    const reportUserInfo = {
      report_user_id: userNo,
    };

    try {
      await api.post(`live_chat/${type}/${type_id}/report/${timestamp}`, reportUserInfo, {
        headers: {
          Authorization: `${accessToken}`,
        },
      });
      alertReport();
    } catch (error) {
      const errorCode = error.response && error.response.data && error.response.data.code;
      if (errorCode === ERROR_CODES.reportDuplicateRequest) {
        alertAlreadyReport();
      } else {
        console.log(errorCode);
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (isMountedRef.current) {
        handleScrollDownButtonClick(); // mount시 최하단으로 스크롤
      }
    }, 500);

    const handleScroll = () => {
      if (contentRef.current) {
        let bottomGap = 115; // 스크롤 바닥에서부터 스크롤 최하단 이동 버튼이 노출될 위치 조절
        const isAtBottom =
          contentRef.current.scrollHeight - bottomGap < contentRef.current.scrollTop + contentRef.current.clientHeight;
        setIsButtonShow(!isAtBottom);
      }
    };

    // ml-video-content-inner에 스크롤 이벤트 추가
    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      }
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    autoResizeTextarea();
  }, [chatText]);

  useEffect(() => {
    if (prevKey === null && nextKey === null) {
      fetchNewLiveChat();
    }
  }, []);

  // useEffect 생성 당시 상태에 nextKey가 없어서 isMounted 추가
  useEffect(() => {
    let isMounted = true;

    const interval = setInterval(() => {
      if (isMounted && nextKey) {
        fetchNewLiveChat();
      }
    }, 6000);

    // 컴포넌트 언마운트할 때 인터벌 클리어
    return () => {
      clearInterval(interval);
      isMounted = false;
    };
  }, [nextKey]);

  useEffect(() => {
    // inView: 스크롤이 채팅 영역 상단 닿았을 때 true
    if (inView && prevKey) {
      fetchPrevLiveChat();
    }
  }, [inView]);

  return (
    <div className={'ml-video-chat-wrapper'}>
      <div className={'ml-video-chat-content-wrapper'}>
        <div ref={contentRef} className='ml-video-chat-content-inner component-custom-scroll'>
          <p className={'chat-welcome-msg'} ref={ref}>
            {t('chat_welcome_msg')}
          </p>

          {chatList?.map((item) => {
            return (
              <div key={item.chatId} className={'video-chat-bubble-group'}>
                <VideoChatBubble item={item} type_id={type_id} onDelete={deleteLiveChat} onReport={reportLiveChat} />
              </div>
            );
          })}
          <ScrollDownButton isShow={isButtonShow} onClick={handleScrollDownButtonClick} />
        </div>
      </div>
      <div className='ml-video-chat-input-wrapper'>
        <div className={'video-chat-input'}>
          <div className='input-group'>
            <textarea
              ref={textareaRef}
              className={'default-input chat-textarea'}
              rows='1'
              maxLength={70}
              placeholder={t('chat_placeholder')}
              onInput={handleInput}
              onKeyDown={handleInput}
              value={chatText}
            ></textarea>
          </div>
          <div className={'button-group'}>
            <button
              type={'button'}
              className={'button-default chat-send-button'}
              aria-label={'Send Chat'}
              onClick={() => {
                sendLiveChat();
              }}
            >
              <span className={'icon-group'}>
                <i
                  className={`icon ${chatText.length === 0 && 'icon-send-gray'} ${chatText.length > 0 && 'icon-send'}`}
                />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoChatWrapper;
